import { zodResolver } from '@hookform/resolvers/zod';
import {
  collectionWriteSchema,
  type Collection,
  type CollectionWriteInput,
} from '@recipes/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
} from '@recipes/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { hueFromName } from '@recipes/domain';
import { HuePicker } from '@/features/appearance/hue';
import { FormField } from '@/features/shared/form-field';
import { HouseholdSelect } from '@/features/shared/household-select';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

type FormValues = z.input<typeof collectionWriteSchema>;

export function CollectionFormDialog({
  open,
  onOpenChange,
  collection,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set the dialog edits this collection. */
  collection?: Collection | null;
  onSaved?: (collection: Collection) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const form = useForm<FormValues, unknown, CollectionWriteInput>({
    resolver: zodResolver(collectionWriteSchema),
    defaultValues: { name: '', description: '', householdId: null, color: null },
  });
  useEffect(() => {
    if (open)
      form.reset({
        name: collection?.name ?? '',
        description: collection?.description ?? '',
        householdId: collection?.householdId ?? null,
        color: collection?.color ?? null,
      });
  }, [open, collection, form]);

  const mutation = useMutation({
    mutationFn: (values: CollectionWriteInput) =>
      collection
        ? api.collections.update(collection.id, { ...values, version: collection.version })
        : api.collections.create(values),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections.all });
      if (collection)
        void queryClient.invalidateQueries({
          queryKey: queryKeys.collections.detail(collection.id),
        });
      onOpenChange(false);
      onSaved?.(saved);
    },
    onError: (e) => toastError(e),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{collection ? t('collections.edit') : t('collections.create')}</DialogTitle>
          <DialogDescription>{t('collections.formDescription')}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={form.handleSubmit((values) =>
            mutation.mutate({ ...values, description: values.description || null }),
          )}
        >
          <FormField
            label={t('collections.fields.name')}
            required
            error={form.formState.errors.name?.message}
          >
            <Input {...form.register('name')} data-testid="collection-name" />
          </FormField>
          <FormField
            label={t('collections.fields.description')}
            optionalText={t('common.optional')}
            error={form.formState.errors.description?.message}
          >
            <Textarea rows={3} {...form.register('description')} />
          </FormField>
          <div>
            <p className="mb-1 text-sm font-medium">{t('appearance.collectionColor')}</p>
            <HuePicker
              label={t('appearance.collectionColor')}
              value={form.watch('color')}
              automatic={hueFromName(form.watch('name') || collection?.name || '')}
              onChange={(hue) => form.setValue('color', hue, { shouldDirty: true })}
            />
          </div>
          {!collection && (
            <HouseholdSelect
              value={form.watch('householdId') ?? null}
              onChange={(v) => form.setValue('householdId', v)}
            />
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending} data-testid="collection-save">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
